import { SpotTrashRecycleBinIcon } from '@tableau/icons/SpotTrashRecycleBinIcon';
import { MenuItemInfo, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { ButtonControlledFlyoutListMenu,
  ButtonControlledFlyoutListMenuProps
} from '~commonInfra/components/ButtonControlledFlyoutListMenu';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~commonInfra/L10n/L10n';
import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { HelpService } from '~commonInfra/services/HelpService';
import { ConnectedQueryParamUpdateProvider } from '~src/bootstrap/routerHelpers/QueryParamUpdateProvider';
import { Messages } from '~src/codegen/Localize';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import {
  RECYCLE_BIN_HELP_DOCS_CATEGORY, RECYCLE_BIN_LEARN_MORE_LINK_TEST_ID
} from '~src/settings/general-settings/components/sections/recycle-bin/RecycleBinSettingSection';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { DatasourceViewTypeDropdownTestIds } from '~src/spacesPlatform/components/Explorer/DatasourceViewTypeDropdown';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { ExplorerDropdown } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerDropdown';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { ExplorerFlyoutPanelProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerFlyoutPanel';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ExplorableItemType, ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { SpaceInfo, SpaceItemForNonEntitySpace, SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

export const DEFAULT_RECYCLE_BIN_CONTENT_TYPE = ExplorableItemTypeValue.workbook;
export const CONTENT_TYPE_SELECT_DROPDOWN_LABEL_ID = 'content-type-dropdown-label';

type RecycleBinContentTypes = 'workbook' | 'project' | 'datasource' | 'flow';

export interface RecyclebinSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly urls: SpacesSiteUrls;
}

export interface RecyclebinSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface RecyclebinSpaceOwnProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export type RecyclebinSpaceProps = RecyclebinSpaceStateProps
  & RecyclebinSpaceDispatchProps
  & RecyclebinSpaceOwnProps;

export interface RecycleBinSpaceState {
  selectedOptionValue: ExplorableItemType;
}

interface RecycleBinContentTypesInfo {
  displayValue: () => string;
  type: ExploreContentApi_ContentType;
}

export function getRecycleBinContentTypeDictionary() {
  const dict: { [V in RecycleBinContentTypes]?: RecycleBinContentTypesInfo } = {
    'workbook': {
      displayValue: Messages.workbooks,
      type: ExplorableItemTypeValue.workbook
    },
    'project': {
      displayValue: Messages.projects,
      type: ExplorableItemTypeValue.project
    },
    'datasource': {
      displayValue: Messages.datasources,
      type: ExplorableItemTypeValue.datasource
    }
  };
  if (FeatureFlags.recycleBinExpEnabled) {
    dict['flow'] = {
      displayValue: Messages.flows,
      type: ExplorableItemTypeValue.flow
    };
  }
  return dict;
}


const RecyclebinSpaceInfo: SpaceInfo = {
  item: SpaceItemForNonEntitySpace,
  spaceType: 'recyclebin',
  subspaceName: undefined
};

const getPanelContent = (handleSelection: (value: ExplorableItemType) => void, flyoutProps: ExplorerFlyoutPanelProps,
                         state: RecycleBinSpaceState): JSX.Element => {
  const menuItems = buildMenuItems(state);
  const panelProps: ButtonControlledFlyoutListMenuProps = {
    onComponentDidMount: _.noop,
    menuItems: menuItems,
    onMenuItemSelected: handleSelection,
    anchorElement: flyoutProps.anchorEl,
    closeMe: flyoutProps.onCloseRequest,
    testId: DatasourceViewTypeDropdownTestIds.childPanelWrapper,
    id: `${flyoutProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
    role: 'menu',
  };
  return <ButtonControlledFlyoutListMenu {...panelProps} />;
};

const getButtonContent = (state: RecycleBinSpaceState): JSX.Element => {
  const dictionary = getRecycleBinContentTypeDictionary();
  return (
    <div>
      {dictionary[state.selectedOptionValue as keyof typeof dictionary].displayValue()}
    </div>
  );
};

function buildMenuItems(state: RecycleBinSpaceState): MenuItemInfo[] {
  const items: MenuItemInfo[] = [];
  for (const contentType of Object.keys(getRecycleBinContentTypeDictionary()) as Array<RecycleBinContentTypes>) {
    const contentTypeInfo = getRecycleBinContentTypeDictionary()[contentType]!;
    items.push({
      value: contentTypeInfo.type,
      displayValue: contentTypeInfo.displayValue(),
      checked: state.selectedOptionValue === contentTypeInfo.type
    });
  }
  return items;
}

export class RecycleBinSpace extends React.PureComponent<RecyclebinSpaceProps, RecycleBinSpaceState> {
  private memoizedButtonContent = defaultMemoize(getButtonContent);
  private memoizedPanelContent = defaultMemoize(getPanelContent);

  constructor(props: RecyclebinSpaceProps) {
    super(props);

    this.state = {
      selectedOptionValue: DEFAULT_RECYCLE_BIN_CONTENT_TYPE
    };

    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.fetchFilteredContent = this.fetchFilteredContent.bind(this);
  }

  public componentDidMount() {
    this.syncStateWithQueryParams();
    this.props.requestSpaceContents();
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.recycleBinBrowserTitle() });
  }

  public componentDidUpdate(prevProps: RecyclebinSpaceProps, prevState: RecycleBinSpaceState) {
    if (prevProps.routeLocationSearch !== this.props.routeLocationSearch) {
      this.syncStateWithQueryParams();
    }

    if (prevState.selectedOptionValue !== this.state.selectedOptionValue) {
      this.fetchFilteredContent();
    }

    if(!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }

  private syncStateWithQueryParams() {
    const validContentType = this.determineContentTypeFromQueryParams();
    if (!validContentType) {
      // user manually edited URL to have invalid contentType, redirect them back to a valid contentType
      this.setState({selectedOptionValue: DEFAULT_RECYCLE_BIN_CONTENT_TYPE});
    } else if (validContentType !== this.state.selectedOptionValue) {
      this.setState({selectedOptionValue: validContentType});
    }
  }

  private determineContentTypeFromQueryParams(): ExplorableItemType | null {
    const queryParams = new URLSearchParams(this.props.routeLocationSearch);
    const contentTypeParam = queryParams.get('contentType');
    if (!contentTypeParam) {
      return DEFAULT_RECYCLE_BIN_CONTENT_TYPE;
    }
    const contentType = contentTypeParam.split(',')[0] as RecycleBinContentTypes;
    return contentType in getRecycleBinContentTypeDictionary()
      ? getRecycleBinContentTypeDictionary()[contentType]!.type
      : null;
  }

  private handleDropdownChange(selectedValue: ExplorableItemType) {
    this.setState({ selectedOptionValue: selectedValue });
  }

  private fetchFilteredContent() {
    const { selectedOptionValue } = this.state;
    this.props.filterSelected([selectedOptionValue]);
  }

  public render(): JSX.Element {
    return (
      <ConnectedSpaceInfoProvider spaceInfo={RecyclebinSpaceInfo}>
        <ConnectedQueryParamUpdateProvider
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
          shouldUpdateBreadcrumbs={false}
          shouldUpdateContentTypes={true}
          shouldUpdateFilters={true}
          shouldUpdateSort={true}
        >
          <div className={SpaceContainerStyles.SpaceContainer}>
            <div className={SpaceContainerStyles.MastheadArea}>
              <SpaceHeaderContent headerText={Messages.recycleBinBrowserTitle()} />
              <div style={SpaceContainerStyles.MastheadSubheadingArea}>
                {Messages.recycleBinMastheadSubheading()}
                <TextLinkWidget target={'_blank'}
                                href={HelpService.getHelpUrlByCategory(RECYCLE_BIN_HELP_DOCS_CATEGORY)}
                                className={learnMoreStyle}
                                testId={RECYCLE_BIN_LEARN_MORE_LINK_TEST_ID}
                >
                  {Messages.learnMore()}
                </TextLinkWidget>
              </div>
            </div>
            <div className={SpaceContainerStyles.SubplaceArea}>
              <ConnectedExplorer
                supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
                emptyStateProps={this.emptyStateProps(this.state.selectedOptionValue)}
                toolbarProps={{
                  supportFilterPanel: true,
                  toolbarContentFilterControl: this.contentFilterControl(),
                  toolbarVisibility: 'enabled',
                  hideSelection: false
                }}
              />
            </div>
          </div>
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private emptyStateProps(contentType: ExplorableItemType): ExplorerEmptyStateProps {
    return {
      icon: SpotTrashRecycleBinIcon,
      primaryMessage: Messages.recycleBinEmptyStatePrimary({
        CONTENT_TYPE:  L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contentType, true))
      }),
      secondaryMessage: Messages.recycleBinEmptyStateSecondary({
        CONTENT_TYPE:  L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contentType, true))
      }),
      actionButtonMessage: Messages.emptyStateCallToAction(),
      learnMorehref: HelpService.getHelpUrlByCategory(RECYCLE_BIN_HELP_DOCS_CATEGORY),
      learnMoreMessage: Messages.learnMore(),
      dataTestId: RECYCLE_BIN_LEARN_MORE_LINK_TEST_ID
    };
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span
          id={ExplorerToolbarTestIds.FilterByTypeSingleSelectButtonLabel}
          className={ExplorerStyles.ToolbarSingleSelectDropdownLabel}
        >
          {Messages.filterByTypeDropdown_label()}
        </span>
        <ExplorerDropdown
          panelTitle={''}
          renderButtonContent={this.renderButtonContent}
          renderPanel={this.renderPanel}
          buttonStyleOverrides={{height: 'auto'}}
          aria-labelledby={CONTENT_TYPE_SELECT_DROPDOWN_LABEL_ID}
          testId={ExplorerToolbarTestIds.FilterByTypeSingleSelectButton}
        />
      </React.Fragment>
    );
  }

  private renderButtonContent = (): JSX.Element => {
    return this.memoizedButtonContent(this.state);
  };

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return this.memoizedPanelContent(this.handleDropdownChange, props, this.state);
  };

  public static contentFilterControlForSiteUserSpace(
    contentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
    defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
    filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void
  ): JSX.Element {
    return (
      <React.Fragment>
        <span
          id={ExplorerToolbarTestIds.FilterByTypeSingleSelectButtonLabel}
          className={ExplorerStyles.ToolbarSingleSelectDropdownLabel}
        >
          {Messages.filterByTypeDropdown_label()}
        </span>
        <ExplorerDropdown
          panelTitle={''}
          renderButtonContent={() => getButtonContent({ selectedOptionValue: contentTypeFilterValues[0] || DEFAULT_RECYCLE_BIN_CONTENT_TYPE })}
          renderPanel={(props) => getPanelContent((value) => filterSelected([value]), props, { selectedOptionValue: contentTypeFilterValues[0] || DEFAULT_RECYCLE_BIN_CONTENT_TYPE })}
          buttonStyleOverrides={{height: 'auto'}}
          aria-labelledby={CONTENT_TYPE_SELECT_DROPDOWN_LABEL_ID}
          testId={ExplorerToolbarTestIds.FilterByTypeSingleSelectButton}
        />
      </React.Fragment>
    );
  }
}